import React, { useState } from 'react';
import { Activity, MessageSquare, LayoutDashboard } from 'lucide-react';
import { AdminDashboard, QueueManagement, SupportManagement } from '../components/features/admin';

/**
 * RepCheck staff console - platform operations only.
 *
 * Team, Plan & usage and the RepGuard sections used to live here, which was
 * wrong: they all read and write the SIGNED-IN USER'S own data through
 * `requireAuth`-only endpoints, so they are customer features, not staff tools.
 * They now live under /settings. Only add something here if it operates across
 * customers or on RepCheck's own infrastructure.
 */
const GROUPS = [
  {
    label: 'Operations',
    items: [
      { id: 'dashboard', label: 'Overview', icon: LayoutDashboard, render: () => <AdminDashboard /> },
      { id: 'queue', label: 'Scan queue', icon: Activity, render: () => <QueueManagement /> },
      { id: 'support', label: 'Support tickets', icon: MessageSquare, render: () => <SupportManagement /> },
    ],
  },
];

const ALL = GROUPS.flatMap((g) => g.items);

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const active = ALL.find((i) => i.id === activeTab) || ALL[0];

  return (
    <main className="p-4 sm:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">Staff console</h1>
        <p className="mt-1 text-sm sm:text-base text-muted-foreground">
          Platform operations across all customers. RepCheck staff only.
        </p>
      </div>

      {/* Mobile: section picker */}
      <div className="md:hidden mb-4">
        <select
          value={activeTab}
          onChange={(e) => setActiveTab(e.target.value)}
          className="w-full rounded-lg border border-input px-3 py-2 text-sm"
        >
          {GROUPS.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.items.map((i) => (
                <option key={i.id} value={i.id}>{i.label}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <div className="flex gap-6">
        {/* Desktop: grouped left rail */}
        <nav className="hidden md:block w-56 shrink-0 space-y-6">
          {GROUPS.map((g) => (
            <div key={g.label}>
              <div className="px-3 mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.label}</div>
              <div className="space-y-1">
                {g.items.map((i) => {
                  const Icon = i.icon;
                  const on = activeTab === i.id;
                  return (
                    <button
                      key={i.id}
                      onClick={() => setActiveTab(i.id)}
                      className={`flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        on ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-200' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <Icon className={`w-[18px] h-[18px] shrink-0 ${on ? 'text-brand-600' : 'text-muted-foreground'}`} />
                      {i.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0">{active.render()}</div>
      </div>
    </main>
  );
};

export default AdminPage;
